import { useState, useEffect, useCallback } from 'react';
import { utterancesService } from '../services/utterances';
import { notificationService } from '../services/notifications';
import { TranscriptUtterance } from '../types';

interface UseUtteranceLoadingProps {
  audioFileId: string;
  hasTranscript: boolean;
  refreshTrigger?: number;
}

export const useUtteranceLoading = ({
  audioFileId,
  hasTranscript,
  refreshTrigger,
}: UseUtteranceLoadingProps) => {
  const [utterances, setUtterances] = useState<TranscriptUtterance[]>([]);
  const [loadingTranscript, setLoadingTranscript] = useState(false);

  const loadUtterances = useCallback(async () => {
    if (!hasTranscript) {
      setUtterances([]);
      setLoadingTranscript(false);
      return;
    }

    try {
      setLoadingTranscript(true);
      const utterances = await utterancesService.getUtterances(audioFileId);
      setUtterances(utterances);
    } catch (err: any) {
      console.error('Failed to load transcript data:', err);
      notificationService.error(
        `Failed to load transcript: ${err.message || 'Unknown error'}`
      );
    } finally {
      setLoadingTranscript(false);
    }
  }, [audioFileId, hasTranscript]);

  useEffect(() => {
    loadUtterances();
  }, [loadUtterances]);

  useEffect(() => {
    if (refreshTrigger) {
      loadUtterances();
    }
  }, [refreshTrigger, loadUtterances]);

  return {
    utterances,
    setUtterances,
    loadingTranscript,
  };
};
